// apps/web/providers/CartProvider.tsx
"use client";

import { createContext, useState, useEffect, ReactNode } from 'react';
import { CartItem } from '@cabukaka/types';
import { cartService } from '../services/cartService';

export interface CartContextType {
  items: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  isLoading: boolean;
  itemCount: number;
  addToCart: (productId: string, quantity?: number, variant?: any) => Promise<void>;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => Promise<void>;
  removeFromCart: (productId: string, variantId?: string) => Promise<void>;
  clearCart: () => Promise<void>;
  isInCart: (productId: string, variantId?: string) => boolean;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(2500);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadCart = async () => {
    setIsLoading(true);
    try {
      const cart = await cartService.getCart();
      setItems(cart.items);
      setSubtotal(cart.subtotal);
      setDeliveryFee(cart.deliveryFee);
      setTotal(cart.total);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  const addToCart = async (productId: string, quantity: number = 1, variant?: any) => {
    await cartService.addToCart(productId, quantity, variant);
    await loadCart();
  };

  const updateQuantity = async (productId: string, quantity: number, variantId?: string) => {
    await cartService.updateQuantity(productId, quantity, variantId);
    await loadCart();
  };

  const removeFromCart = async (productId: string, variantId?: string) => {
    await cartService.removeFromCart(productId, variantId);
    await loadCart();
  };

  const clearCart = async () => {
    await cartService.clearCart();
    await loadCart();
  };

  const isInCart = (productId: string, variantId?: string): boolean => {
    return items.some(item => {
      if (variantId) {
        return item.id === variantId;
      }
      return item.productId === productId && !item.variantId;
    });
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      subtotal,
      deliveryFee,
      total,
      isLoading,
      itemCount,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      isInCart
    }}>
      {children}
    </CartContext.Provider>
  );
}